import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { defaultRangeExtractor, useVirtualizer, type Virtualizer } from "@tanstack/react-virtual";
import {
  CheckCircle2,
  CircleDashed,
  FileText,
  HelpCircle,
  Languages,
  Mic,
  Pencil,
  RotateCw,
  XCircle,
} from "lucide-react";
import { AnchoredPopover, ClickPopover, PopoverHeading, VIRTUAL_THRESHOLD, cx, useToast } from "@amber/ui";
import type {
  EvidenceRecord,
  ProblemView,
  SpeakerView,
  TranslationView,
  TurnCheck,
  TurnView,
} from "../../contracts/index";
import type { Editing } from "../editing/Editing";
import { TurnEditor } from "../editing/TurnEditor";
import { TurnNotes } from "../editing/TurnNotes";
import { WordCorrection } from "../editing/WordCorrection";
import { LANE_DOTS, clock } from "../format";
import type { PlayerState } from "./Player";
import { locatorDetail, paragraphLabel } from "./DocumentView";
import { CheckContent, PassageContent, SpeakerContent, WordContent } from "./Popovers";
import { entryIndexOfTurn, transcriptEntries, withMounted, type TranscriptEntry } from "./transcriptEntries";

const HIGHLIGHT_KEY = "amber.evidence.highlightUncertainty";
const TURN_HEIGHT_ESTIMATE = 80;

function Switch({
  label,
  checked,
  onChange,
  disabled,
  title,
  icon,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  title?: string;
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      title={title}
      onClick={() => {
        onChange(!checked);
      }}
      className="inline-flex h-7 flex-none items-center gap-1.5 rounded-md border border-divider px-1.5 text-md whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60"
    >
      {icon}
      {label}
      <span
        aria-hidden="true"
        className={cx("relative h-3.5 w-6 rounded-full transition-colors", checked ? "bg-link" : "bg-surface-3")}
      >
        <span
          className={cx(
            "absolute top-0.5 size-2.5 rounded-full bg-bg transition-[left]",
            checked ? "left-3" : "left-0.5",
          )}
        />
      </span>
    </button>
  );
}

const CHECK_GLYPHS: Record<TurnCheck["mark"], React.ReactNode> = {
  verified: <CheckCircle2 size={12} aria-hidden="true" className="text-done" />,
  failed: <XCircle size={12} aria-hidden="true" className="text-attention" />,
  rechecking: <RotateCw size={12} aria-hidden="true" className="motion-safe:animate-[spin_1.6s_linear_infinite]" />,
  unverified: <CircleDashed size={12} aria-hidden="true" />,
};

function CheckMark({ check, where }: { check: TurnCheck; where: string }) {
  return (
    <ClickPopover
      label={`Passage check at ${where}: ${check.label}`}
      contentClassName="w-[360px]"
      className={cx(
        "inline-flex min-h-6 items-center gap-1 rounded-md px-1.5 text-md whitespace-nowrap hover:bg-surface-3 aria-expanded:bg-surface-3",
        check.mark === "failed" ? "font-strong text-fg" : "text-muted",
      )}
      trigger={
        <>
          {CHECK_GLYPHS[check.mark]}
          {check.label}
        </>
      }
    >
      <CheckContent check={check} />
    </ClickPopover>
  );
}

function Legend() {
  return (
    <ClickPopover
      label="Legend and shortcuts"
      trigger={<HelpCircle size={15} aria-hidden="true" />}
      className="inline-flex size-7 items-center justify-center rounded-md border border-divider text-muted"
    >
      <PopoverHeading>Markings</PopoverHeading>
      <ul className="m-0 flex list-none flex-col gap-1 p-0 text-md">
        <li>
          <span className="underline decoration-dotted underline-offset-2">Speaker</span> · dotted: uncertain
          attribution (below 0.80)
        </li>
        <li>
          <span className="underline decoration-dotted decoration-muted underline-offset-[3px]">word</span> · dotted
          underline: low confidence word
        </li>
        <li>
          <span className="inline-block h-3 w-1 border-l-2 border-dashed border-waiting align-middle" /> dashed edge:
          low transcription confidence in the passage
        </li>
        <li>
          <span className="inline-block h-3 w-1 border-l-2 border-dotted border-attention align-middle" /> dotted edge:
          flagged passage
        </li>
        <li>[silence …] and [not transcribed …] mark ranges without words; they are valid results</li>
        <li>
          <span className="underline decoration-link underline-offset-[3px]">word</span> · solid underline: corrected by
          a person
        </li>
        <li className="flex items-center gap-1">
          <CheckCircle2 size={12} aria-hidden="true" className="text-done" /> Verified · passage checks passed
        </li>
        <li className="flex items-center gap-1">
          <XCircle size={12} aria-hidden="true" className="text-attention" /> Check failed · a passage check failed
        </li>
        <li>Highlight uncertainty adds a stronger tint to these marks; the quiet marks always stay</li>
      </ul>
      <PopoverHeading>Keyboard</PopoverHeading>
      <p className="text-md">
        Space or K play/pause · J / L back/forward 10 s · [ ] previous/next turn · + − zoom tracks · T adjust timing
      </p>
      <p className="text-md">
        In a focused turn: ← → choose a word, Enter corrects it, C corrects the turn · ⌘Enter save · Esc cancel
      </p>
    </ClickPopover>
  );
}

interface VirtualList {
  readonly virtualizer: Virtualizer<HTMLDivElement, Element>;
  readonly inner: RefObject<HTMLDivElement | null>;
}

function renderEntries<E>(entries: readonly E[], virtual: VirtualList | null, render: (entry: E) => ReactNode) {
  if (!virtual) return entries.map(render);
  const { virtualizer, inner } = virtual;
  return (
    <div ref={inner} className="relative" style={{ height: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map((item) => {
        const entry = entries[item.index];
        return entry === undefined ? null : (
          <div
            key={item.key}
            data-index={item.index}
            ref={virtualizer.measureElement}
            className="absolute top-0 left-0 w-full"
            style={{ transform: `translateY(${String(item.start - virtualizer.options.scrollMargin)}px)` }}
          >
            {render(entry)}
          </div>
        );
      })}
    </div>
  );
}

function readOnlyReason(record: EvidenceRecord, translationOn: boolean, canEdit: boolean): string | null {
  if (record.pinned) return "Older versions are read-only.";
  if (translationOn) return "Corrections apply to the original-language text; switch off the translation to correct.";
  if (!record.actions.correct.enabled) return record.actions.correct.reason;
  return canEdit ? null : "Viewers can read and play, but not correct.";
}

function transcriptHint(isDocument: boolean, canCorrect: boolean): string {
  if (isDocument)
    return canCorrect
      ? "Click a paragraph number to find it in the archived document. Click a word to correct it; C or Correct edits the whole contribution."
      : "Click a paragraph number to find it in the archived document.";
  return canCorrect
    ? "Click a timestamp or segment to play. Click a word to correct it; C or Correct edits the whole turn."
    : "Click a timestamp or segment to play. Click a word for its confidence.";
}

function TranscriptToolbar({
  record,
  label,
  translationOn,
  onTranslation,
  follow,
  setFollow,
  highlight,
  setHighlight,
}: {
  record: EvidenceRecord;
  label: string;
  translationOn: boolean;
  onTranslation: (on: boolean) => void;
  follow: boolean;
  setFollow: (on: boolean) => void;
  highlight: boolean;
  setHighlight: (on: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 border-b border-divider px-3 py-2 max-[640px]:flex-wrap">
      {record.document ? (
        <FileText size={14} aria-hidden="true" className="text-muted" />
      ) : (
        <Mic size={14} aria-hidden="true" className="text-muted" />
      )}
      <h2 id="transcript-label" className="m-0 mr-auto min-w-0 truncate text-ui font-semibold" title={label}>
        {label}
      </h2>
      <Switch
        label="English"
        icon={<Languages size={13} aria-hidden="true" />}
        checked={translationOn}
        disabled={!record.actions.translate.enabled}
        {...(record.actions.translate.enabled ? {} : { title: record.actions.translate.reason })}
        onChange={onTranslation}
      />
      {record.document ? null : <Switch label="Follow" checked={follow} onChange={setFollow} />}
      <Switch
        label="Highlight uncertainty"
        checked={highlight}
        onChange={(v) => {
          setHighlight(v);
          localStorage.setItem(HIGHLIGHT_KEY, v ? "1" : "0");
        }}
      />
      <Legend />
    </div>
  );
}

type Entry = TranscriptEntry<TurnView, EvidenceRecord["gaps"][number]>;

function useEntryVirtualizer({
  items,
  scroller,
  inner,
  keep,
  enabled,
}: {
  items: readonly Entry[];
  scroller: HTMLDivElement | null;
  inner: RefObject<HTMLDivElement | null>;
  keep: readonly number[];
  enabled: boolean;
}) {
  return useVirtualizer({
    count: items.length,
    getScrollElement: () => scroller,
    estimateSize: () => TURN_HEIGHT_ESTIMATE,
    getItemKey: (i) => {
      const entry = items[i];
      return entry?.kind === "turn" ? entry.turn.idx : `gap-${String(entry?.gap.start_ms ?? i)}`;
    },
    rangeExtractor: (range) => withMounted(defaultRangeExtractor(range), keep),
    scrollMargin: inner.current?.offsetTop ?? 0,
    overscan: 6,
    enabled,
  });
}

function FollowPill({ onFollow }: { onFollow: () => void }) {
  return (
    <button
      type="button"
      onClick={onFollow}
      className="sticky bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-link px-3 py-1 text-md text-on-primary shadow-pop"
    >
      Follow playback
    </button>
  );
}

export function TranscriptPanel({
  record,
  turns,
  speakers,
  player,
  selected,
  onSelect,
  translation,
  translationState,
  translationOn,
  onTranslation,
  canEdit,
  editing,
  onResolve,
  onMoreCorrections,
}: {
  record: EvidenceRecord;
  turns: readonly TurnView[];
  speakers: readonly SpeakerView[];
  player: PlayerState;
  selected: number | null;
  onSelect: (idx: number) => void;
  translation: TranslationView | null;
  translationState: string | null;
  translationOn: boolean;
  onTranslation: (on: boolean) => void;
  canEdit: boolean;
  editing: Editing;
  onResolve: (p: ProblemView) => void;
  onMoreCorrections: (turn: TurnView) => void;
}) {
  const [follow, setFollow] = useState(true);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const toast = useToast();
  const [highlight, setHighlight] = useState(
    () => typeof localStorage !== "undefined" && localStorage.getItem(HIGHLIGHT_KEY) === "1",
  );
  const list = useRef<HTMLDivElement>(null);
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const listRef = useCallback((el: HTMLDivElement | null) => {
    list.current = el;
    setScroller(el);
  }, []);
  const inner = useRef<HTMLDivElement>(null);
  const programmatic = useRef(false);
  const playing = [...turns].reverse().find((t) => t.start_ms <= player.timeMs && player.timeMs < t.end_ms + 400);
  const active = player.playing ? (playing?.idx ?? selected) : (selected ?? playing?.idx ?? null);
  const items = useMemo(() => transcriptEntries(turns, record.gaps), [turns, record.gaps]);
  const virtual = turns.length > VIRTUAL_THRESHOLD;
  const editingEntry = entryIndexOfTurn(items, editingIdx);
  const activeEntry = virtual ? entryIndexOfTurn(items, active) : -1;
  const virtualizer = useEntryVirtualizer({
    items,
    scroller,
    inner,
    keep: [editingEntry, activeEntry],
    enabled: virtual,
  });

  useEffect(() => {
    if (!follow || active === null || !list.current) return;
    if (virtual) {
      if (activeEntry < 0) return;
      programmatic.current = true;
      virtualizer.scrollToIndex(activeEntry, { align: "auto" });
    } else {
      const el = list.current.querySelector<HTMLElement>(`[data-turn="${String(active)}"]`);
      if (!el) return;
      programmatic.current = true;
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
    window.setTimeout(() => {
      programmatic.current = false;
    }, 600);
  }, [active, follow, virtual, activeEntry, virtualizer]);

  const speakerOf = (id: string | null) => speakers.find((s) => s.id === id) ?? null;
  const label = translationOn && translation ? translation.label : (record.transcript?.label ?? "Transcript");
  const readOnly = readOnlyReason(record, translationOn, canEdit);
  const done = (message: string | null) => {
    setEditingIdx(null);
    if (message) toast.show(message);
  };

  return (
    <section aria-labelledby="transcript-label" className="flex min-h-0 flex-1 flex-col">
      <TranscriptToolbar
        record={record}
        label={label}
        translationOn={translationOn}
        onTranslation={onTranslation}
        follow={follow}
        setFollow={setFollow}
        highlight={highlight}
        setHighlight={setHighlight}
      />
      <p className="m-0 border-b border-divider bg-surface px-3 py-1.5 text-md text-muted">
        {translationOn
          ? (translationState ?? "Machine translation · not verbatim. Corrections apply to the original-language text.")
          : transcriptHint(Boolean(record.document), readOnly === null)}
      </p>
      <div
        ref={listRef}
        tabIndex={-1}
        className="relative min-h-0 flex-1 overflow-y-auto px-2 py-2"
        onWheel={() => {
          if (!programmatic.current && follow && player.playing) setFollow(false);
        }}
      >
        {renderEntries(items, virtual ? { virtualizer, inner } : null, (entry) =>
          entry.kind === "gap" ? (
            <p key={`gap-${String(entry.gap.start_ms)}`} className="m-0 px-3 py-1 font-mono text-md text-muted">
              {entry.gap.label}
            </p>
          ) : (
            <TurnBlock
              key={entry.turn.idx}
              turn={entry.turn}
              speaker={speakerOf(entry.turn.speaker_id)}
              active={active === entry.turn.idx}
              highlight={highlight}
              translated={translationOn && translation ? (translation.segments[String(entry.turn.idx)] ?? null) : null}
              player={player}
              onSelect={onSelect}
              readOnly={readOnly}
              record={record}
              editing={editing}
              isEditing={editingIdx === entry.turn.idx}
              onEdit={() => {
                setEditingIdx(entry.turn.idx);
              }}
              onDone={done}
              onMore={onMoreCorrections}
              onResolve={onResolve}
              canEdit={canEdit}
            />
          ),
        )}
        <p className="m-0 px-3 pt-3 pb-1 text-md text-muted">End of transcript · {turns.length} turns</p>
        {!follow && player.playing ? (
          <FollowPill
            onFollow={() => {
              setFollow(true);
            }}
          />
        ) : null}
      </div>
    </section>
  );
}

function TurnText({
  turn,
  highlight,
  player,
  readOnly,
  record,
  editing,
  onEdit,
  onDone,
}: {
  turn: TurnView;
  highlight: boolean;
  player: PlayerState;
  readOnly: string | null;
  record: EvidenceRecord;
  editing: Editing;
  onEdit: () => void;
  onDone: (message: string | null) => void;
}) {
  const [focus, setFocus] = useState(0);
  const [open, setOpen] = useState<number | null>(null);
  const container = useRef<HTMLParagraphElement>(null);
  const anchor =
    open === null ? null : (container.current?.querySelector<HTMLElement>(`[data-w="${String(open)}"]`) ?? null);
  const word = open === null ? undefined : turn.words[open];
  const current = turn.words[focus];
  return (
    <>
      <p
        ref={container}
        tabIndex={0}
        aria-label={`Turn ${String(turn.n)} text. Left and right arrow keys choose a word, Enter ${readOnly === null ? "corrects it, C corrects the whole turn" : "shows its details"}.`}
        className="m-0 rounded-sm text-base leading-[1.55] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        onClick={(e) => {
          const target = (e.target as HTMLElement).closest<HTMLElement>("[data-w]");
          if (!target) return;
          const i = Number(target.dataset["w"]);
          setFocus(i);
          setOpen(i);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight") setFocus(Math.min(turn.words.length - 1, focus + 1));
          else if (e.key === "ArrowLeft") setFocus(Math.max(0, focus - 1));
          else if (e.key === "Enter") setOpen(focus);
          else if ((e.key === "c" || e.key === "C") && readOnly === null && !e.metaKey && !e.ctrlKey) onEdit();
          else return;
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {turn.words.map((w, i) => (
          <Fragment key={w.i}>
            {i > 0 ? " " : null}
            <span
              data-w={i}
              className={cx(
                "cursor-pointer rounded-[3px] hover:bg-surface-3",
                w.low && "underline decoration-muted decoration-dotted underline-offset-[3px]",
                w.corrected && "underline decoration-link underline-offset-[3px]",
                w.low && highlight && "bg-waiting-soft",
                i === focus &&
                  "in-focus-visible:bg-accent-soft in-focus-visible:outline-1 in-focus-visible:outline-focus",
                open === i && "bg-accent-soft",
              )}
            >
              {w.text}
              {w.corrected ? <span className="sr-only"> (corrected)</span> : null}
            </span>
          </Fragment>
        ))}
      </p>
      <span className="sr-only" aria-live="polite">
        {current && document.activeElement === container.current
          ? `${current.text}${current.low ? `, low confidence, ${current.confidence?.toFixed(2) ?? ""}` : ""}`
          : ""}
      </span>
      {word ? (
        <AnchoredPopover
          anchor={anchor}
          open={open !== null}
          label={`Word: ${word.text}`}
          onOpenChange={(o) => {
            if (!o) {
              setOpen(null);
              container.current?.focus();
            }
          }}
        >
          <WordContent
            word={word}
            onPlay={() => {
              player.seek(word.start_ms, true);
            }}
            readOnlyNote={readOnly}
            correction={
              readOnly === null && open !== null ? (
                <WordCorrection
                  record={record}
                  turn={turn}
                  position={open}
                  editing={editing}
                  onDone={(m) => {
                    setOpen(null);
                    onDone(m);
                  }}
                />
              ) : null
            }
          />
        </AnchoredPopover>
      ) : null}
    </>
  );
}

function PassageMark({ turn, highlight }: { turn: TurnView; highlight: boolean }) {
  const mark = turn.passage.mark;
  if (mark === "none") return null;
  return (
    <ClickPopover
      label={
        mark === "flagged"
          ? (turn.passage.reasons[0] ?? "Flagged passage")
          : `Low transcription confidence in this passage, ${turn.passage.confidence?.toFixed(2) ?? ""}`
      }
      trigger={
        <span
          className={cx(
            "block h-full w-[3px]",
            mark === "flagged"
              ? "border-l-[3px] border-dotted border-attention"
              : "border-l-[3px] border-dashed border-waiting",
            highlight && "w-[6px] bg-waiting-soft",
          )}
        />
      }
      className="absolute top-1 bottom-1 left-[66px] flex w-6 justify-center"
    >
      <PassageContent turn={turn} />
    </ClickPopover>
  );
}

function TurnAnchor({
  turn,
  anchor,
  detail,
  player,
  onSelect,
}: {
  turn: TurnView;
  anchor: string | null;
  detail: string | null;
  player: PlayerState;
  onSelect: (idx: number) => void;
}) {
  if (anchor)
    return (
      <button
        type="button"
        onClick={() => {
          onSelect(turn.idx);
        }}
        aria-label={`Paragraph ${anchor.replace("¶", "")}${detail ? `, ${detail}` : ""}: find it in the archived document`}
        title={`Paragraph ${anchor.replace("¶", "")}${detail ? `, ${detail}` : ""}`}
        className="absolute top-2 left-3 w-[60px] text-left font-mono text-md leading-tight font-semibold text-link"
      >
        {anchor}
        {detail ? <span className="block text-2xs font-normal text-muted">{detail}</span> : null}
      </button>
    );
  return (
    <button
      type="button"
      onClick={() => {
        onSelect(turn.idx);
        player.seek(turn.start_ms, true);
      }}
      aria-label={`Play from ${clock(turn.start_ms)}`}
      className="absolute top-2 left-3 font-mono text-md font-semibold text-link"
    >
      {clock(turn.start_ms)}
    </button>
  );
}

function speakerDot(speaker: SpeakerView | null): string {
  if (!speaker?.name) return "text-muted";
  return LANE_DOTS[speaker.lane % LANE_DOTS.length] ?? "text-link";
}

function TurnTools({
  turn,
  anchor,
  readOnly,
  pinned,
  onEdit,
}: {
  turn: TurnView;
  anchor: string | null;
  readOnly: string | null;
  pinned: boolean;
  onEdit: () => void;
}) {
  return (
    <span className="ml-auto flex items-center gap-2">
      {turn.language ? <span className="font-mono text-2xs text-muted uppercase">{turn.language}</span> : null}
      {turn.check ? <CheckMark check={turn.check} where={anchor ?? clock(turn.start_ms)} /> : null}
      {turn.unit_id && !pinned ? (
        <button
          type="button"
          disabled={readOnly !== null}
          title={readOnly ?? "Correct this turn (C)"}
          onClick={onEdit}
          className="inline-flex min-h-6 items-center gap-1 rounded-md px-1 text-md text-link disabled:cursor-not-allowed disabled:text-muted"
        >
          <Pencil size={12} aria-hidden="true" />
          Correct
        </button>
      ) : null}
    </span>
  );
}

function TurnSpeaker({
  turn,
  speaker,
  highlight,
  player,
  children,
}: {
  turn: TurnView;
  speaker: SpeakerView | null;
  highlight: boolean;
  player: PlayerState;
  children: ReactNode;
}) {
  const uncertainName = turn.attribution.uncertain;
  const printed = turn.mention;
  return (
    <div className="mb-0.5 flex flex-wrap items-center gap-x-1.5 text-md">
      <span aria-hidden="true" className={cx("text-[9px]", speakerDot(speaker))}>
        ●
      </span>
      {speaker ? (
        <ClickPopover
          label={`${printed ?? speaker.display}${uncertainName ? ", uncertain attribution" : ""}`}
          trigger={
            <span
              className={cx(
                "inline-flex min-h-6 items-center font-semibold",
                !speaker.name && !printed && "text-muted",
                uncertainName && "underline decoration-dotted underline-offset-2",
                uncertainName && highlight && "bg-waiting-soft",
              )}
            >
              {printed ?? speaker.display}
            </span>
          }
        >
          <SpeakerContent
            speaker={speaker}
            confirmed={turn.confirmed}
            onSeek={(ms) => {
              player.seek(ms);
            }}
          />
        </ClickPopover>
      ) : (
        <span className="font-semibold text-muted">Unattributed</span>
      )}
      {speaker?.role && !printed ? <span className="text-muted">{speaker.role.value}</span> : null}
      {printed && speaker && !speaker.name ? <span className="text-muted">· {speaker.kind_label}</span> : null}
      {turn.overlap ? <span className="text-muted">· overlapping</span> : null}
      {children}
    </div>
  );
}

function TurnBlock({
  turn,
  speaker,
  active,
  highlight,
  translated,
  player,
  onSelect,
  readOnly,
  record,
  editing,
  isEditing,
  onEdit,
  onDone,
  onMore,
  onResolve,
  canEdit,
}: {
  turn: TurnView;
  speaker: SpeakerView | null;
  active: boolean;
  highlight: boolean;
  translated: string | null;
  player: PlayerState;
  onSelect: (idx: number) => void;
  readOnly: string | null;
  record: EvidenceRecord;
  editing: Editing;
  isEditing: boolean;
  onEdit: () => void;
  onDone: (message: string | null) => void;
  onMore: (turn: TurnView) => void;
  onResolve: (p: ProblemView) => void;
  canEdit: boolean;
}) {
  const anchor = paragraphLabel(turn);
  const detail = locatorDetail(turn);
  const printed = turn.mention;
  if (isEditing) {
    return (
      <article
        data-turn={turn.idx}
        aria-label={`Editing turn ${String(turn.n)}`}
        className="relative mb-1 rounded-lg bg-accent-soft/60 py-2 pr-3 pl-[78px] shadow-[inset_0_0_0_1px_var(--color-link)]"
      >
        <span className="absolute top-2 left-3 font-mono text-md font-semibold text-muted">
          {anchor ?? clock(turn.start_ms)}
        </span>
        <TurnEditor
          record={record}
          turn={turn}
          editing={editing}
          onDone={onDone}
          onMore={() => {
            onMore(turn);
          }}
        />
      </article>
    );
  }
  return (
    <article
      data-turn={turn.idx}
      aria-current={active ? "true" : undefined}
      aria-label={`Turn ${String(turn.n)}, ${printed ?? speaker?.display ?? "unattributed"}, ${anchor ?? clock(turn.start_ms)}`}
      className={cx(
        "relative mb-1 rounded-lg py-2 pr-3 pl-[78px]",
        active && "bg-row-selected shadow-[inset_3px_0_0_var(--color-link)]",
      )}
    >
      <PassageMark turn={turn} highlight={highlight} />
      <TurnAnchor turn={turn} anchor={anchor} detail={detail} player={player} onSelect={onSelect} />
      <TurnSpeaker turn={turn} speaker={speaker} highlight={highlight} player={player}>
        <TurnTools turn={turn} anchor={anchor} readOnly={readOnly} pinned={Boolean(record.pinned)} onEdit={onEdit} />
      </TurnSpeaker>
      {translated !== null ? (
        <p className="m-0 text-base leading-[1.55] italic">{translated}</p>
      ) : (
        <TurnText
          turn={turn}
          highlight={highlight}
          player={player}
          readOnly={readOnly}
          record={record}
          editing={editing}
          onEdit={onEdit}
          onDone={onDone}
        />
      )}
      {translated === null ? (
        <TurnNotes record={record} turn={turn} canEdit={canEdit && !record.pinned} onResolve={onResolve} />
      ) : null}
    </article>
  );
}
